/**
 * 视讯 (provider_code='huanhai') 专属入场逻辑: launch 拿 session_id → SSE 拿登录凭据
 *
 * 跟 useGamePlay() 不是一回事: 普通厂牌拿到 launch_url 直接塞 iframe 展示;
 * 视讯没有 launch_url, 要把 SSE 拿到的账号密码交给 /live/* 现有的 USLO00 WS 登录
 * (src/services/websocket/senders/uslo.ts), 玩家无感进入, 见
 * `docs/25-live-frontend-integration.md` 第二/三节 (v4, 2026-09-18)。
 *
 * v4 新增: 拿到 session_id 起必须持续发 `POST /api/v1/game/heartbeat` 保活
 * (每 25 秒, 见 startLiveHeartbeat), 跟 `/api/v1/user/heartbeat` 是两个不同接口,
 * 60 秒不发这个视讯专属心跳, 后端会认定玩家已离场、自动把钱带回并踢出厂牌——
 * 玩家会跳到视讯自己的 H5 去玩, 完全离开我方页面, 不能假设页面还在等于玩家还在。
 *
 * 已接入实际入口: MainLobbyMobile.vue 的 goProvider() 调用 enterLive() → 存进
 * liveLoginBridge.ts 的一次性凭据交接 → 跳 /live/login, 该页面 onMounted 取出后
 * 自动调用 userStore.login(username, password), 走现有 USLO00 登录、WS 协议不用改。
 * 离开 /live/* 时调用 exitLive() (Navbar.vue/SettingsSidebar.vue 的退出登录按钮),
 * session_id 靠 liveLoginBridge.ts 的模块级共享值跨组件传递 (见 exitLive 函数注释)。
 *
 * ⚠️ `enterLive()` resolve 出的 username/password 是明文凭据, 用完即弃:
 * 不要写进 localStorage/sessionStorage/cookie, 不要打进 console/埋点/日志/错误上报
 * (这条约束已经在 waitLiveToken 里遵守, 调用方接手后也要遵守到底)。
 *
 * 调用方需要处理：
 * - `enterLive()` 正常 resolve → 拿到 `{ username, password, access_token, h5_url, is_new_player }`。
 * - 抛出 `LiveLaunchError` → launch 本身业务失败 (余额不足/已有会话/厂牌问题等),
 *   `e.message` 直接是后端返回的可读文案 (如"余额不足"), 直接展示即可, 不用再翻译一遍。
 * - 抛出 `LiveTokenTimeoutError` → 提示「进入失败，请重试」，**不要**自动重连/重新 launch。
 * - 抛出 `LiveSessionInvalidError` → session_id 已过期或不属于当前用户，可以自动重新 launch 一次。
 * - `enterLive()` 返回 `null` (不抛错) → 只有"重复点击、上一次还没处理完"这一种情况, 静默忽略即可。
 */
import { ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useLobbyStore } from '@/stores/modules/lobby'
import { launchGame, exitGame, gameHeartbeat } from '@/services/api/game'
import { getBalance } from '@/services/api/wallet'
import { waitLiveToken, LiveTokenTimeoutError, LiveSessionInvalidError } from '@/services/sse/liveToken'
import { setLiveSessionId, getLiveSessionId, clearLiveSessionId } from '@/composables/liveLoginBridge'
import { setCachedBalance } from '@/composables/useBalanceCache'
import { formatBalanceWithoutSymbol } from '@/utils/balance'
import type { LiveTokenEvent } from '@/types/api'

// 视讯厂牌固定 provider_code, 不要跟具体某个视讯游戏的 game_code 混淆 (视讯没有 game_code)
export const LIVE_PROVIDER_CODE = 'huanhai'

// launch 接口业务失败 (如 110001 余额不足, 200002 已有游戏会话, 200005/200008 厂牌问题等):
// message 直接用后端返回的 msg, 不在前端另建一套码表 —— 后端的文案已经是可读的中文,
// 没必要重复维护
export class LiveLaunchError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'LiveLaunchError'
  }
}

// 200002 已有游戏会话: 一个账号同时只能在一个厂牌里, 详见
// docs/25-live-frontend-integration.md 错误码表
const ALREADY_HAS_SESSION_CODE = 200002

// exit 接口返回 code=0 只表示后端"已受理"带出请求, 真正把会话标记为已结束是网关异步
// 回调完成的, 文档写的是"通常 1 秒内" (docs/25-live-frontend-integration.md 第五节)。
// 退出后立刻重新 launch 很可能撞上这个传播延迟又拿到一次 200002, 所以带一点退避重试,
// 不要一次不中就直接放弃报错。
const RELAUNCH_RETRY_DELAYS_MS = [500, 1000]

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

// 视讯游戏心跳保活 (POST /api/v1/game/heartbeat) —— 跟 useHeartbeat.ts 的大厅 HTTP
// token 心跳、client.ts 的视讯 WS 心跳都不是一回事, 三者互不替代:
// launch 那一刻钱就全额划到视讯侧了, 后端有个每 30 秒跑一次的回收任务, 一旦这个
// session 的 last_heartbeat 超过 60 秒没更新就认定玩家已离场, 自动把钱带回、
// 强制结束会话 (docs/25-live-frontend-integration.md 第五节, 2026-09-17 实测真的
// 因为漏发这个心跳被强制踢出过)。视讯玩家会跳到视讯自己的 H5 里玩, 完全离开了
// 我方页面, 所以这个心跳必须由我们自己在拿到 session_id 后主动、持续地发,
// 不能像普通厂牌 iframe 那样假设"页面还在就等于玩家还在"。
// 用模块级变量存 timer (不用组件局部变量): enterLive()/exitLive() 经常跨组件调用
// (原因见 exitLive 函数注释), 计时器必须能被后面调用它的任何一个组件实例停掉。
const LIVE_HEARTBEAT_INTERVAL_MS = 25_000
let liveHeartbeatTimer: ReturnType<typeof setInterval> | null = null

function startLiveHeartbeat(sessionId: string): void {
  stopLiveHeartbeat()
  liveHeartbeatTimer = setInterval(() => {
    gameHeartbeat(sessionId)
  }, LIVE_HEARTBEAT_INTERVAL_MS)
}

function stopLiveHeartbeat(): void {
  if (liveHeartbeatTimer) {
    clearInterval(liveHeartbeatTimer)
    liveHeartbeatTimer = null
  }
}

export function useLiveGamePlay() {
  const lobbyStore = useLobbyStore()
  const { activeCurrency } = storeToRefs(lobbyStore)

  const entering = ref(false)
  const sessionId = ref('')

  /**
   * 进入视讯: launch 拿 session_id, 立刻连 SSE 等登录凭据 (不要等, 凭据通常 1 秒内到)。
   * 成功后 sessionId 已保存供 exitLive() 使用; 拿到 username/password 后怎么登录见文件头说明。
   *
   * 200002 已有游戏会话的静默恢复: 只有在本地还留着上一次视讯会话的 session_id
   * (liveLoginBridge.ts 缓存的, 说明是本机之前那次视讯没走完退出流程就断了, 残留在
   * 后端) 时才能自愈——调一次 exitGame 收掉它再重试一次 launch。如果本地没有缓存,
   * 说明冲突的是别的设备/别的厂牌的会话: exit 接口的 session_id 是必传字段, 也没有
   * "强制退出当前账号所有会话"这种接口, 前端根本不知道该退哪个, 只能照后端文档的
   * 要求把 msg 原样提示给玩家、引导他自己去把那边退出 (docs/25-live-frontend-
   * integration.md 错误码表), 不能瞎重试。
   */
  async function enterLive(): Promise<LiveTokenEvent | null> {
    if (entering.value) return null
    entering.value = true
    try {
      const currency = activeCurrency.value || lobbyStore.currencies[0]?.code || 'USDT'
      const launchParams = { provider_code: LIVE_PROVIDER_CODE, currency, platform: 'mobile' as const }

      let res = await launchGame(launchParams)

      if (!res.success && res.code === ALREADY_HAS_SESSION_CODE) {
        const staleSid = getLiveSessionId()
        if (staleSid) {
          console.warn('[视讯] 检测到本地残留的视讯会话, 静默退出后重试 launch:', staleSid)
          clearLiveSessionId()
          await exitGame(staleSid)
          res = await launchGame(launchParams)

          // exit 生效有传播延迟, 第一次重试还是 200002 的话再退避重试几次
          for (const delayMs of RELAUNCH_RETRY_DELAYS_MS) {
            if (res.success || res.code !== ALREADY_HAS_SESSION_CODE) break
            console.warn(`[视讯] 退出后 launch 仍是已有会话, ${delayMs}ms 后重试`)
            await sleep(delayMs)
            res = await launchGame(launchParams)
          }
        } else {
          // 本地没有缓存的 session_id: 冲突的是别的设备/别的厂牌的会话, 前端不知道
          // 该退哪个 (exit 的 session_id 必传, 也没有强制退出接口), 只能把后端原文
          // 提示给玩家, 见文件头注释
          console.warn('[视讯] launch 返回已有游戏会话, 但本地没有缓存的 session_id (可能是刷新过页面, 或冲突会话在别处), 无法静默处理')
        }
      }

      if (!res.success || !res.data) {
        // 带上后端真实的错误文案 (如"余额不足"/"已有游戏会话"), 不要吞掉只留 null 让调用方猜
        throw new LiveLaunchError(res.error || '进入视讯失败')
      }
      sessionId.value = res.data.session_id
      setLiveSessionId(res.data.session_id)
      // 从拿到 session_id 这一刻起就要开始保活, 不能等 SSE 令牌到了才起——
      // SSE 最长可能等 60 秒, 跟回收任务的踢出阈值一样长, 等它反而可能错过第一次心跳
      startLiveHeartbeat(res.data.session_id)
      return await waitLiveToken(res.data.session_id)
    } catch (e) {
      // 走到这里说明这次进入视讯没有成功 (launch 失败/令牌超时/会话失效), 不会再用
      // 这个 session_id 了, 停掉心跳——留着只会继续无意义地打接口, 且可能跟下一次
      // enterLive() 自己起的心跳并发冲突
      stopLiveHeartbeat()
      if (e instanceof LiveLaunchError) {
        console.error('[视讯] launch 失败:', e.message)
      } else if (e instanceof LiveTokenTimeoutError) {
        // 钱已全额转入视讯侧, 后端 5 分钟内会自动带回主钱包, 不要自动重试/重新 launch
        console.error('[视讯] 等待令牌超时, 稍后重试')
      } else if (e instanceof LiveSessionInvalidError) {
        // session_id 已过期/不属于当前用户: 应重新 launch, 而不是重试同一个 session_id
        console.error('[视讯] 会话已失效, 需要重新 launch')
      } else {
        console.error('[视讯] 进入失败:', e)
      }
      throw e
    } finally {
      entering.value = false
    }
  }

  /**
   * 退出视讯: 页面关闭/离开视讯相关路由时务必调用, 否则钱一直留在视讯侧,
   * 且下次进任何厂牌都会被 `200002 已有游戏会话` 挡住。
   *
   * session_id 优先取 liveLoginBridge.ts 里的模块级共享值: enterLive() 在
   * MainLobbyMobile.vue 里调用, 而 exitLive() 常常是在完全不同的组件树
   * (Navbar.vue/SettingsSidebar.vue) 里调用的全新组合式函数实例, 局部
   * sessionId ref 是空的, 必须靠这个跨组件共享的值。
   *
   * `keepalive: true` 给 App.vue 的 pagehide 尽力退出用: 页面已经在卸载了,
   * 普通 fetch 会被浏览器直接中断, 这个选项能让请求脱离文档生命周期继续发出去
   * (是否真的送达仍不保证, 见 App.vue 里的说明)。
   */
  async function exitLive(options: { keepalive?: boolean } = {}): Promise<void> {
    stopLiveHeartbeat()
    const sid = getLiveSessionId() || sessionId.value
    clearLiveSessionId()
    sessionId.value = ''
    if (!sid) return
    try {
      await exitGame(sid, options)
    } catch (e) {
      console.warn('[视讯] 退出接口调用失败:', e)
    }
  }

  return { entering, sessionId, enterLive, exitLive }
}

/**
 * 断开视讯 WS、调退出接口回收资金/会话、用大厅 JWT 纠正被视讯登录覆盖的字段。
 *
 * 视讯是跟大厅共用 userStore 的独立账号体系: 任何"视讯这边出问题了, 得退出"的
 * 兜底路径 (路由守卫兜底、断网、下注/进桌超时、拒绝协议等) 都绝不能直接调
 * userStore.logout()/httpLogout() —— 那会把大厅账号也一起清空/登出、甚至跳去
 * 大厅自己的 /login (历史上这几处兜底真的这么写过, 是本函数存在的原因)。
 * 统一收在这里, 调用方 await 完之后自行 router.replace('/') 即可, 不用每处
 * 都重新拼一遍 disconnect+清表+exitLive+restoreHttpLogin 这几步。
 */
export async function exitLiveAndRestoreLobby(): Promise<void> {
  const [{ useWebSocketStore }, { useUserStore }, { useTableStore }] = await Promise.all([
    import('@/stores/modules/websocket'),
    import('@/stores'),
    import('@/stores/modules/table'),
  ])
  const wsStore = useWebSocketStore()
  const userStore = useUserStore()
  const tableStore = useTableStore()

  wsStore.disconnect()
  tableStore.clear()
  await useLiveGamePlay().exitLive()
  await userStore.restoreHttpLogin()
  await refreshLobbyBalanceAfterExit()
}

/**
 * 视讯退出后刷新大厅余额显示。
 *
 * exit 接口返回 code=0 只表示后端"已受理"带出请求, 资金真正到账要等网关回调,
 * 文档写的是"通常 1 秒内" (docs/25-live-frontend-integration.md 第六节)。退出后
 * 立刻查一次余额很可能查早了、钱还没到账, 所以这里查两次: 一次立刻查并等它返回
 * (大多数情况已经够快, 回大厅时就能看到正确值), 一次隔 1.5 秒兜底纠正 (不 await,
 * 避免让调用方多等 1.5 秒才能跳转)。写的是 MainLobbyMobile.vue 用的那份 SWR 缓存
 * (useBalanceCache.ts), 不直接改 userStore.balance —— 那个字段是视讯 WS (USLO06)
 * 自己在用的, 跟大厅余额显示是两套互不相关的状态。
 */
async function refreshLobbyBalanceAfterExit(): Promise<void> {
  const lobbyStore = useLobbyStore()
  const currency = lobbyStore.activeCurrency || lobbyStore.currencies[0]?.code
  if (!currency) return

  const fetchOnce = async () => {
    const res = await getBalance(currency)
    if (res.success && res.data) {
      setCachedBalance(currency, formatBalanceWithoutSymbol(res.data.cash_balance))
    }
  }

  await fetchOnce()
  setTimeout(fetchOnce, 1500)
}
